import './ClassList.less';

import { DragOutlined } from '@ant-design/icons';
import {
  closestCenter,
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable';
import {
  Button,
  Form,
  Space,
  Tag,
  Typography,
} from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  useCarrierClasses,
  useCarrierClassesReorder,
  useCarrierUpdate,
  useClassCreate,
  useClassRemove,
  useClassUpdate,
  useClassVehicleMapUpdate,
} from '../../hooks/apiHooks';
import { UserRole } from '../../models/User';
import Container from '../common/Container';
import EditButton from '../common/EditButton';
import PermissionsToggle from '../common/PermissionsToggle';
import ClassModal from '../modals/ClassModal';
import DraggableVehicleChip from './DraggableVehicleChip';
import DroppableUnassignedContainer from './DroppableUnassignedContainer';
import SortableRateClassCard from './SortableRateClassCard';

/*
interface IProps {
  id: string;
  carrier: Carrier;
  userRole?: UserRole;
  vehicles: Vehicle[];
  classVehicleMap: ClassVehicles;
  onHeaderActions?: (actions: ReactNode) => void;
}
*/

const classListCollisionDetection = (args) => {
  const activeType = args.active.data.current?.type;

  if (activeType === 'class') {
    return closestCenter(args).filter((collision) => {
      const container = args.droppableContainers.find(
        (c) => c.id === collision.id
      );
      return container?.data?.current?.type === 'class';
    });
  }

  const pointerCollisions = pointerWithin(args);
  const collisions =
    pointerCollisions.length > 0 ? pointerCollisions : rectIntersection(args);

  return collisions.filter((collision) => {
    const container = args.droppableContainers.find(
      (c) => c.id === collision.id
    );
    const type = container?.data?.current?.type;
    return type === 'rate-class' || type === 'unassigned';
  });
};

const ClassList = ({
  carrier,
  classVehicleMap,
  id,
  onHeaderActions,
  userRole,
  vehicles,
}) => {
  const { carrierClasses } = useCarrierClasses(carrier?.id);
  const [orderedClasses, setOrderedClasses] = useState([]);
  const createMutation = useClassCreate();
  const updateMutation = useClassUpdate();
  const removeMutation = useClassRemove();
  const reorderMutation = useCarrierClassesReorder();
  const carrierUpdateMutation = useCarrierUpdate();
  const updateCarrierVehicleMapMutation = useClassVehicleMapUpdate();

  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);
  const [deletableClass, setDeletableClass] = useState();
  const [showAddModal, setShowAddModal] = useState(false);
  const [classNames, setClassNames] = useState({});
  const [formValues, setFormValues] = useState({});
  const [activeId, setActiveId] = useState(null);
  const originalFormData = useRef({});
  const originalClassNames = useRef({});
  const originalClassOrder = useRef([]);
  const carrierRef = useRef(carrier);
  carrierRef.current = carrier;

  useEffect(() => {
    const fetchedClasses = carrierClasses ?? [];
    setOrderedClasses((prev) => {
      if (!isEditing) {
        return fetchedClasses;
      }
      const serverById = new Map(fetchedClasses.map((c) => [c.id, c]));
      const next = prev
        .filter((c) => serverById.has(c.id))
        .map((c) => serverById.get(c.id));
      const prevIds = new Set(prev.map((c) => c.id));
      fetchedClasses.forEach((c) => {
        if (!prevIds.has(c.id)) {
          next.push(c);
        }
      });
      return next;
    });
  }, [carrierClasses, isEditing]);

  const classes = orderedClasses;

  const initializeClassNames = useCallback(() => {
    const names = {};
    classes.forEach((c) => {
      names[c.id] = c.name;
    });
    return names;
  }, [classes]);

  // find classes with changed names
  const changedClassNames = useMemo(() => {
    const changes = [];
    classes.forEach((c) => {
      if (classNames[c.id] && classNames[c.id] !== c.name) {
        changes.push({ ...c, name: classNames[c.id] });
      }
    });
    return changes;
  }, [classNames, classes]);

  // checking if any class name is empty
  const hasEmptyClassName = useMemo(() => {
    return classes.some((c) => !classNames[c.id]?.trim());
  }, [classNames, classes]);

  const onAdd = useCallback(
    (classification) => {
      classification.carrierId = carrier.id;
      createMutation.mutate(classification);
    },
    [carrier.id, createMutation]
  );

  const onDelete = useCallback(() => {
    if (deletableClass) {
      removeMutation.mutate(deletableClass);
    }
  }, [deletableClass, removeMutation]);

  const onSubmit = useCallback(
    async (classVehicles) => {
      // prevent save if any class name is empty
      if (hasEmptyClassName) {
        return;
      }

      // first, update any changed class names
      for (const changedClass of changedClassNames) {
        await updateMutation.mutateAsync(changedClass);
      }

      const originalIds = originalClassOrder.current.map((c) => c.id);
      const currentIds = classes.map((c) => c.id);
      const orderChanged =
        originalIds.length !== currentIds.length ||
        originalIds.some((id, i) => id !== currentIds[i]);

      if (orderChanged) {
        await reorderMutation.mutateAsync({
          carrierId: carrier.id,
          classIds: currentIds,
        });
      }

      // then update vehicle assignments
      updateCarrierVehicleMapMutation.mutate(classVehicles);
    },
    [
      updateCarrierVehicleMapMutation,
      changedClassNames,
      updateMutation,
      hasEmptyClassName,
      classes,
      carrier.id,
      reorderMutation,
    ]
  );

  const handleSave = useCallback(() => {
    if (hasEmptyClassName) {
      return;
    }
    form.submit();
  }, [form, hasEmptyClassName]);

  const handleEdit = useCallback(() => {
    // store orig values before editing
    originalFormData.current = form.getFieldsValue();
    originalClassNames.current = { ...classNames };
    originalClassOrder.current = [...classes];
    setFormValues(originalFormData.current);
    setIsEditing(true);
  }, [form, classNames, classes]);

  const handleCancel = useCallback(() => {
    // revert form values
    form.setFieldsValue(originalFormData.current);
    setFormValues(originalFormData.current);
    // revert class names and order
    setClassNames(originalClassNames.current);
    setOrderedClasses(originalClassOrder.current);
    setDeletableClass(undefined);
    setIsEditing(false);
  }, [form]);

  const handleClassNameChange = useCallback((classId, newName) => {
    setClassNames((prev) => ({
      ...prev,
      [classId]: newName,
    }));
  }, []);

  const onTogglePermission = useCallback(
    (permission) => {
      const newCarrier = {
        ...carrierRef.current,
        classPermission: permission,
      };
      carrierUpdateMutation.mutate(newCarrier);
    },
    [carrierUpdateMutation]
  );

  useEffect(() => {
    form.setFieldsValue(classVehicleMap);
    setFormValues(classVehicleMap || {});
  }, [classVehicleMap, form]);

  // initialize class names when classes change
  useEffect(() => {
    setClassNames(initializeClassNames());
  }, [initializeClassNames]);

  useEffect(() => {
    if (!isEditing) {
      setDeletableClass(undefined);
    }
  }, [isEditing]);

  useEffect(() => {
    if (createMutation.isSuccess) {
      setShowAddModal(false);
    }
  }, [createMutation.isSuccess]);

  // turn off editing mode when vehicle map is successfully saved
  useEffect(() => {
    if (updateCarrierVehicleMapMutation.isSuccess) {
      setIsEditing(false);
    }
  }, [updateCarrierVehicleMapMutation.isSuccess]);

  const isSaving =
    updateCarrierVehicleMapMutation.isLoading ||
    updateMutation.isLoading ||
    reorderMutation.isLoading;

  const sortableClassIds = useMemo(
    () => classes.map((c) => c.id),
    [classes]
  );

  const currentVehicleMap = useMemo(() => {
    if (isEditing) {
      return formValues;
    }
    // filter classVehicleMap to only include classes that are in carrier.classes
    const filteredMap = {};
    const classIds = new Set(classes.map((c) => String(c.id)));
    Object.keys(classVehicleMap || {}).forEach((classId) => {
      if (classIds.has(String(classId))) {
        filteredMap[classId] = classVehicleMap[classId];
      }
    });
    return filteredMap;
  }, [isEditing, formValues, classVehicleMap, classes]);

  const assignedVehicleIds = useMemo(() => {
    const allAssigned = Object.values(currentVehicleMap || {}).flat();
    return new Set(allAssigned);
  }, [currentVehicleMap]);

  const unassignedVehicles = useMemo(() => {
    return vehicles.filter((v) => !assignedVehicleIds.has(v.id));
  }, [vehicles, assignedVehicleIds]);

  const getAssignedVehiclesForClass = useCallback(
    (classId) => {
      const vehicleIds = formValues[classId] || [];
      return vehicles.filter((v) => vehicleIds.includes(v.id));
    },
    [vehicles, formValues]
  );

  const handleDragStart = useCallback((event) => {
    setActiveId(event.active.id);
  }, []);

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      setActiveId(null);

      if (!over || !isEditing) {
        return;
      }

      const activeData = active.data.current;
      const overData = over.data.current;

      if (!activeData) {
        return;
      }

      // Reordering rate class cards
      if (activeData.type === 'class') {
        if (active.id === over.id) {
          return;
        }

        const oldIndex = classes.findIndex((c) => c.id === active.id);
        const newIndex = classes.findIndex((c) => c.id === over.id);

        if (oldIndex === -1 || newIndex === -1) {
          return;
        }

        setOrderedClasses(arrayMove(classes, oldIndex, newIndex));
        return;
      }

      if (!overData) {
        return;
      }

      // dragging vehicle type from unassigned to a rate class
      if (activeData.type === 'vehicle' && overData.type === 'rate-class') {
        const vehicleId = activeData.vehicle.id;
        const targetClassId = overData.classId;

        // get current assignments
        const currentValues = form.getFieldsValue();
        const currentClassVehicles = currentValues[targetClassId] || [];

        // Don't add if already assigned
        if (currentClassVehicles.includes(vehicleId)) {
          return;
        }

        // Remove from previous class if assigned to another class
        const updatedValues = { ...currentValues };
        Object.keys(updatedValues).forEach((classId) => {
          if (classId !== targetClassId && Array.isArray(updatedValues[classId])) {
            updatedValues[classId] = updatedValues[classId].filter((id) => id !== vehicleId);
          }
        });

        // add to target class
        updatedValues[targetClassId] = [...currentClassVehicles, vehicleId];

        form.setFieldsValue(updatedValues);
        setFormValues(updatedValues);
      }

      // Moving a vehicle type back to unassigned
      if (activeData.type === 'vehicle' && overData.type === 'unassigned') {
        const vehicleId = activeData.vehicle.id;

        const currentValues = form.getFieldsValue();
        const updatedValues = { ...currentValues };

        Object.keys(updatedValues).forEach((classId) => {
          if (Array.isArray(updatedValues[classId])) {
            updatedValues[classId] = updatedValues[classId].filter((id) => id !== vehicleId);
          }
        });

        form.setFieldsValue(updatedValues);
        setFormValues(updatedValues);
      }
    },
    [form, isEditing, classes]
  );

  const handleRemoveVehicle = useCallback(
    (classId, vehicleId) => {
      const currentValues = form.getFieldsValue();
      const currentClassVehicles = (currentValues[classId] || []).filter((id) => id !== vehicleId);
      const updatedValues = {
        ...currentValues,
        [classId]: currentClassVehicles,
      };
      form.setFieldsValue(updatedValues);
      setFormValues(updatedValues);
    },
    [form]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const headerActions = useMemo(
    () => (
      <Space>
        {userRole === UserRole.SUPER_ADMIN && (
          <PermissionsToggle
            defaultValue={carrier.classPermission}
            submitting={carrierUpdateMutation.isLoading}
            onChange={onTogglePermission}
          />
        )}
        {isEditing ? (
          <>
            <EditButton
              disabled={hasEmptyClassName}
              loading={isSaving}
              onClick={handleSave}
            >
              Save
            </EditButton>
            <Button onClick={handleCancel}>
              Cancel
            </Button>
          </>
        ) : (
          <EditButton onClick={handleEdit}>
            Edit
          </EditButton>
        )}
        <Button
          ghost
          style={{ width: 120 }}
          type="primary"
          onClick={() => setShowAddModal(true)}
        >
          Add Class
        </Button>
      </Space>
    ),
    [
      userRole,
      carrier.classPermission,
      carrierUpdateMutation.isLoading,
      isEditing,
      hasEmptyClassName,
      isSaving,
      onTogglePermission,
      handleSave,
      handleCancel,
      handleEdit,
    ]
  );

  const headerActionsRef = useRef(headerActions);
  headerActionsRef.current = headerActions;

  useEffect(() => {
    if (onHeaderActions) {
      onHeaderActions(headerActionsRef.current);
    }
  }, [
    onHeaderActions,
    userRole,
    carrier.classPermission,
    carrierUpdateMutation.isLoading,
    isEditing,
    hasEmptyClassName,
    isSaving,
  ]);

  const activeVehicle = useMemo(() => {
    if (!activeId || typeof activeId !== 'string' || !activeId.startsWith('vehicle-')) {
      return null;
    }
    const vehicleId = activeId.replace('vehicle-', '');
    return vehicles.find((v) => String(v.id) === vehicleId) || null;
  }, [activeId, vehicles]);

  const activeClass = useMemo(() => {
    if (
      activeId == null ||
      (typeof activeId === 'string' && activeId.startsWith('vehicle-'))
    ) {
      return null;
    }
    return (
      classes.find(
        (c) => c.id === activeId || String(c.id) === String(activeId)
      ) || null
    );
  }, [activeId, classes]);

  const activeClassAssignedVehicles = useMemo(() => {
    if (!activeClass) {
      return [];
    }
    return getAssignedVehiclesForClass(activeClass.id);
  }, [activeClass, getAssignedVehiclesForClass]);

  return (
    <Container id={id}>
      <Form form={form} layout="vertical" onFinish={onSubmit}>

        <DndContext
          collisionDetection={classListCollisionDetection}
          sensors={sensors}
          onDragEnd={handleDragEnd}
          onDragStart={handleDragStart}
        >
          <div className="class-list__grid">
            {/* unassigned vehicle types */}
            <div className="class-list__unassigned-column">
              <div className="class-list__unassigned-header">
                <Typography.Text strong>Unassigned Vehicle Types</Typography.Text>
              </div>
              <DroppableUnassignedContainer isEditing={isEditing}>
                {unassignedVehicles.length === 0 ? (
                  <Typography.Text
                    style={{ display: 'block', padding: '20px', textAlign: 'center' }}
                    type="secondary"
                  >
                    No unassigned vehicles
                  </Typography.Text>
                ) : (
                  <Space wrap size={[8, 8]}>
                    {unassignedVehicles.map((vehicle) => (
                      <DraggableVehicleChip key={vehicle.id} isEditing={isEditing} vehicle={vehicle} />
                    ))}
                  </Space>
                )}
              </DroppableUnassignedContainer>
            </div>

            <SortableContext items={sortableClassIds} strategy={rectSortingStrategy}>
              <div className="class-list__rate-classes-grid">
                {classes.map((classification, index) => (
                  <SortableRateClassCard
                    key={classification.id}
                    assignedVehicles={getAssignedVehiclesForClass(classification.id)}
                    classification={classification}
                    deletableClass={deletableClass}
                    editedName={classNames[classification.id]}
                    index={index}
                    isEditing={isEditing}
                    isRemoving={removeMutation.isLoading}
                    vehicles={vehicles}
                    onClassNameChange={handleClassNameChange}
                    onDelete={onDelete}
                    onDeleteCancel={() => setDeletableClass(undefined)}
                    onDeleteClick={setDeletableClass}
                    onRemoveVehicle={handleRemoveVehicle}
                  />
                ))}
              </div>
            </SortableContext>
          </div>
          <DragOverlay>
            {activeVehicle ? (
              <Tag className="class-list__vehicle-chip">
                <Space size={4}>
                  {isEditing && <DragOutlined />}
                  {activeVehicle.name}
                </Space>
              </Tag>
            ) : activeClass ? (
              <div className="class-list__rate-class-container class-list__rate-class-overlay">
                <div className="class-list__rate-class-header">
                  <Space>
                    <DragOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
                    <span>
                      {classNames[activeClass.id] || activeClass.name}
                    </span>
                  </Space>
                </div>
                <div className="class-list__rate-class-drop-zone editing">
                  {activeClassAssignedVehicles.length > 0 ? (
                    <Space wrap size={[8, 8]}>
                      {activeClassAssignedVehicles.map((vehicle) => (
                        <Tag key={vehicle.id} className="class-list__vehicle-chip">
                          {vehicle.name}
                        </Tag>
                      ))}
                    </Space>
                  ) : (
                    <Typography.Text
                      style={{ display: 'block', padding: '20px', textAlign: 'center' }}
                      type="secondary"
                    >
                      Drop vehicle types here
                    </Typography.Text>
                  )}
                </div>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </Form>
      {showAddModal && (
        <ClassModal
          classes={classes}
          error={createMutation.error}
          submitting={createMutation.isLoading}
          onClose={() => setShowAddModal(false)}
          onSubmit={onAdd}
        />
      )}
    </Container>
  );
};

export default ClassList;
